/**
 * What is written on the bar the caret stands in, as opposed to what is written in it.
 *
 * Every control states its change at that one bar, so a barline, a repeat, a jump or a
 * rehearsal mark lands where the user is looking rather than always at bar 1. With no caret there is
 * no bar to write to, and the panel says so instead of offering controls that would refuse.
 *
 * The clef is the odd one here: it belongs to a staff of a part rather than to the bar, so
 * it is written to the staff the caret's voice is on, from this bar onwards.
 */

import { ALTO, BASS, TENOR, TREBLE, bars, partById, partMeasureAt } from "@app/score-editor";
import type { Caret, Clef, MeasureId, PartMeasure, Score } from "@app/score-editor";
import type { Command } from "@app/score-editor";
import { Choice, Text } from "./ScoreSetupFields";
import type { Option } from "./ScoreSetupFields";
import {
  BARLINES,
  MOST_PASSES,
  SIGNS,
  endingNumbers,
  nth,
  pickupLengths,
} from "../lib/score-menus-bars";

const CLEFS: readonly { value: string; label: string; clef: Clef | null }[] = [
  { value: "none", label: "No change", clef: null },
  { value: "treble", label: "Treble", clef: TREBLE },
  { value: "bass", label: "Bass", clef: BASS },
  { value: "alto", label: "Alto", clef: ALTO },
  { value: "tenor", label: "Tenor", clef: TENOR },
];

const JUMPS: readonly Option<string>[] = [
  { value: "none", label: "Plays on" },
  { value: "daCapo", label: "D.C." },
  { value: "dalSegno", label: "D.S." },
];

const BREAKS: readonly Option<string>[] = [
  { value: "none", label: "As engraved" },
  { value: "system", label: "New system" },
  { value: "page", label: "New page" },
];

/**
 * How many times a repeated section is played. The first choice states no count, which
 * leaves it to the ending bars and the verses under the section: twice, or as many passes as
 * they number.
 */
function timesOptions(stated: number | null): readonly Option<string>[] {
  const counts = [...Array(MOST_PASSES - 1).keys()].map((at) => at + 2);
  if (stated !== null && !counts.includes(stated)) counts.push(stated);
  return [
    { value: "none", label: "As endings and verses say" },
    ...counts.map((count) => ({ value: String(count), label: `${count} times` })),
  ];
}

const TOGGLE =
  "h-10 flex-1 rounded-[10px] border px-2 text-[12px] disabled:cursor-default disabled:text-ink-quiet";

/** The bar of the part the caret stands in, and the staff its voice is on. */
function under(score: Score, caret: Caret): { measure: PartMeasure | null; staff: number } {
  const part = partById(score, caret.partId);
  const measure = part === null ? null : partMeasureAt(part, caret.measureId);
  return { measure, staff: measure?.voices[caret.voiceIndex]?.staff ?? 0 };
}

/** The clef this staff changes to at this bar, if it changes at all. */
function clefKey(measure: PartMeasure | null, staff: number): string {
  const written = measure?.clefChanges[staff] ?? null;
  const found = CLEFS.find(
    (candidate) =>
      candidate.clef !== null &&
      written !== null &&
      candidate.clef.sign === written.sign &&
      candidate.clef.line === written.line,
  );
  return found?.value ?? "none";
}

export interface ScoreInspectorBarProps {
  readonly score: Score;
  readonly caret: Caret | null;
  readonly index: number;
  edit(built: Command | string, label?: string): void;
}

export default function ScoreInspectorBar({
  score,
  caret,
  index,
  edit,
}: ScoreInspectorBarProps): JSX.Element {
  if (caret === null) {
    return (
      <p className="text-xs leading-relaxed text-ink-quiet">
        Click a bar to write a barline, a repeat or a rehearsal mark on it.
      </p>
    );
  }

  const at: MeasureId = caret.measureId;
  const measure = score.measures[index] ?? null;
  const repeat = measure?.repeat ?? null;
  const endings = repeat?.endings ?? [];
  const jump = measure?.jump ?? null;

  const toggleLook = (on: boolean): string =>
    on
      ? `${TOGGLE} border-ink bg-ink text-white`
      : `${TOGGLE} border-rule text-ink-faded hover:border-ink-quiet hover:text-ink`;

  const place = under(score, caret);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <Choice
          label="Barline"
          value={measure?.barline ?? "regular"}
          options={BARLINES.map((barline) => ({ value: barline.style, label: barline.label }))}
          className="min-w-[116px] flex-1"
          onChange={(style) => {
            const found = BARLINES.find((barline) => barline.style === style);
            if (found === undefined) return;
            edit(bars.setBarline(score, at, found.style), "barline");
          }}
        />
        <Choice
          label="Break"
          value={measure?.breakBefore ?? "none"}
          options={BREAKS}
          className="min-w-[116px] flex-1"
          onChange={(chosen) =>
            edit(
              bars.setBreak(score, at, chosen === "system" || chosen === "page" ? chosen : null),
              "break",
            )
          }
        />
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          className={toggleLook(repeat?.start === true)}
          onClick={() => edit(bars.setRepeatStart(score, at, repeat?.start !== true), "repeat")}
        >
          Repeat from
        </button>
        <button
          type="button"
          className={toggleLook(repeat?.end === true)}
          onClick={() => edit(bars.setRepeatEnd(score, at, repeat?.end !== true), "repeat")}
        >
          Repeat to
        </button>
      </div>

      {repeat?.end === true && (
        <Choice
          label="Played"
          value={repeat.times === null ? "none" : String(repeat.times)}
          options={timesOptions(repeat.times)}
          onChange={(chosen) =>
            edit(
              bars.setRepeatTimes(score, at, chosen === "none" ? null : Number(chosen)),
              "repeat count",
            )
          }
        />
      )}

      {/* A bar can be in more than one ending, so each number is a toggle of its own. */}
      <div className="grid grid-cols-3 gap-2">
        {endingNumbers(score, endings).map((number) => (
          <button
            key={number}
            type="button"
            className={toggleLook(endings.includes(number))}
            onClick={() =>
              edit(
                bars.setEndings(
                  score,
                  at,
                  endings.includes(number)
                    ? endings.filter((held) => held !== number)
                    : [...endings, number],
                ),
                "ending",
              )
            }
          >
            {nth(number)} time
          </button>
        ))}
      </div>

      {/* Four toggles rather than one choice: a bar can carry more than one of them, and
          the last bar of a piece is often both the end of the coda and the Fine. */}
      <div className="flex gap-2">
        {SIGNS.map((sign) => (
          <button
            key={sign.key}
            type="button"
            className={toggleLook(jump?.[sign.key] === true)}
            onClick={() =>
              edit(bars.setJump(score, at, { [sign.key]: jump?.[sign.key] !== true }), sign.short)
            }
          >
            {sign.short}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Choice
          label="End of this bar"
          value={jump?.jump ?? "none"}
          options={JUMPS}
          className="min-w-[116px] flex-1"
          onChange={(picked) =>
            edit(
              bars.setJump(score, at, {
                jump: picked === "daCapo" || picked === "dalSegno" ? picked : null,
              }),
              "jump",
            )
          }
        />
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Text
          label="Rehearsal mark"
          value={measure?.rehearsal ?? ""}
          placeholder="A"
          className="min-w-[116px] flex-1"
          onCommit={(text) => edit(bars.setRehearsal(score, at, text), "rehearsal mark")}
        />
        <Choice
          label="Bar lasts"
          value={String(measure?.length ?? "full")}
          options={[
            { value: "full", label: "Its full metre" },
            ...pickupLengths(score, index).map((length) => ({
              value: String(length.ticks),
              label: length.label,
            })),
          ]}
          className="min-w-[116px] flex-1"
          onChange={(picked) =>
            edit(bars.setLength(score, at, picked === "full" ? null : Number(picked)), "bar length")
          }
        />
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Choice
          label="Clef here"
          value={clefKey(place.measure, place.staff)}
          options={CLEFS.map((clef) => ({ value: clef.value, label: clef.label }))}
          className="min-w-[116px] flex-1"
          onChange={(picked) => {
            const found = CLEFS.find((candidate) => candidate.value === picked);
            if (found === undefined) return;
            edit(bars.setClefAt(score, caret.partId, at, place.staff, found.clef), "clef");
          }}
        />
      </div>
    </div>
  );
}
